import { Trans } from '@lingui/react';
import { Stack } from '@startuptools/ui/Stack';
import { FormControl } from '@startuptools/ui/FormControl';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { Chip } from '@startuptools/ui/Chip';
import { Input } from '@startuptools/ui/Input';
import { Button } from '@startuptools/ui/Button';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Typography } from '@startuptools/ui/Typography';
import { Alert } from '@startuptools/ui/Alert';
import { IconButton } from '@startuptools/ui/IconButton';
import { Box } from '@startuptools/ui/Box';
import { GenericError } from '@startuptools/ui/Error';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import EditIcon from '@mui/icons-material/Edit';
import { Controller, useForm } from 'react-hook-form';
import { type GqlCreateIdentityInput, useCurrentUserSuspenseQuery } from '../graphql/react-operations';
import { SelectIdentity } from '@startuptools/business-ui/identity/select/SelectIdentity';
import { useTimeout } from '@startuptools/common/react-hooks';
import React from 'react';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';
import { JuridicalKind } from '../models/identity.model';
import { type ApolloError } from '@apollo/client';

type FormData = {
  regFormPaymentThroughBolago: boolean;
  contact: GqlCreateIdentityInput | undefined | null;
  signatory: GqlCreateIdentityInput | undefined | null;
  signatureSameAsContact: boolean;
};

const CreateButton: React.ComponentProps<typeof SelectIdentity>['createButton'] = props => (
  <IconButton {...props}>
    <EditIcon />
  </IconButton>
);

export const BvContactForm = ({
  onSave,
  defaultValues,
  submitLabel = <Trans id="Save & Continue" />,
  loading,
  error,
}: {
  defaultValues: Partial<Omit<FormData, 'signatureSameAsContact'>>;
  onSave: (data: Partial<Omit<FormData, 'signatureSameAsContact'>>) => Promise<void>;
  submitLabel?: React.ReactNode;
  loading: boolean;
  error: ApolloError | undefined;
}) => {
  const {
    data: { currentUser: user },
  } = useCurrentUserSuspenseQuery();
  const { control, setValue, handleSubmit, watch } = useForm<FormData>({
    defaultValues: {
      ...defaultValues,
      contact: defaultValues.contact ? identityToInput(defaultValues.contact) : undefined,
      signatory: defaultValues.signatory ? identityToInput(defaultValues.signatory) : undefined,

      signatureSameAsContact: Boolean(
        !defaultValues.signatory ||
          (defaultValues.contact && defaultValues.signatory && defaultValues.contact.id === defaultValues.signatory.id),
      ),
    },
  });

  const [success, setSuccess] = React.useState(false);

  useTimeout(
    () => {
      if (success) setSuccess(false);
    },
    success ? 3000 : null,
  );

  const currentSignatureSameAsContact = watch('signatureSameAsContact');
  const currentContact = watch('contact');
  const currentSignatory = watch('signatory');

  return (
    <Stack gap={2}>
      <Typography level="h4">
        <Trans id="Who is assigned as contact person on the subscription form?" />
      </Typography>
      <form
        onSubmit={handleSubmit(async ({ regFormPaymentThroughBolago, contact, signatory, signatureSameAsContact }) => {
          await onSave({
            regFormPaymentThroughBolago,
            contact,
            signatory: signatureSameAsContact ? contact : signatory,
          });
          setSuccess(true);
        })}
      >
        <Stack gap={2} maxWidth="50%">
          <Controller
            control={control}
            name="contact"
            render={({ field }) => (
              <SelectIdentity
                createButton={CreateButton}
                juridicalKinds={[JuridicalKind.Person]}
                identity={field.value}
                value={field.value?.id}
                multiple={false}
                handleCreate={async contact => {
                  field.onChange(contact);
                  setValue('contact.phone', contact.phone ?? '');
                  setValue('contact.email', contact.email ?? '');
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="contact.email"
            rules={{ required: true }}
            render={({ field, formState }) => (
              <FormControl error={Boolean(formState.errors.contact?.email)} disabled={!currentContact}>
                <FormLabel>
                  <Trans id="Email" />
                </FormLabel>

                <Input startDecorator={<EmailIcon />} {...field} value={field.value ?? ''} />
              </FormControl>
            )}
          />

          <Controller
            control={control}
            name="contact.phone"
            render={({ field, formState }) => (
              <FormControl error={Boolean(formState.errors.contact?.phone)} disabled={!currentContact}>
                <FormLabel>
                  <Trans id="Phone" />
                </FormLabel>

                <Input startDecorator={<PhoneIcon />} {...field} value={field.value ?? ''} />
              </FormControl>
            )}
          />

          {user.admin && (
            <Controller
              name="regFormPaymentThroughBolago"
              control={control}
              render={({ field: { value, onChange, ...props } }) => (
                <Checkbox
                  {...props}
                  checked={value}
                  onChange={e => onChange(e.target.checked)}
                  label={
                    <Trans
                      id="Payment through Bolago <chip>admin</chip>"
                      components={{
                        chip: <Chip size="sm" color="neutral" variant="outlined" />,
                      }}
                    />
                  }
                />
              )}
            />
          )}

          <Typography level="h4">
            <Trans id="Who signs the subscription form?" />
          </Typography>

          <Alert>
            <Trans id="Note: this only needs to be 1 person, even when the company normally requires multiple authorized signatories." />
          </Alert>

          <Controller
            name="signatureSameAsContact"
            control={control}
            render={({ field: { value, ...props } }) => (
              <FormControl>
                <Checkbox
                  {...props}
                  checked={value}
                  onChange={e => {
                    if (!e.target.checked) {
                      setValue('signatory', null);
                    } else {
                      setValue('signatory', currentContact);
                    }
                    return props.onChange(e.target.checked);
                  }}
                  label={<Trans id="Same as contact person" />}
                />
              </FormControl>
            )}
          />

          {!currentSignatureSameAsContact && (
            <>
              <Controller
                control={control}
                name="signatory"
                rules={{ required: true }}
                render={({ field, formState }) => (
                  <FormControl error={Boolean(formState.errors.signatory)}>
                    <SelectIdentity
                      createButton={CreateButton}
                      juridicalKinds={[JuridicalKind.Person]}
                      identity={field.value}
                      value={field.value?.id}
                      multiple={false}
                      handleCreate={async signatory => {
                        field.onChange(signatory);
                        setValue('signatory.phone', signatory.phone ?? '');
                        setValue('signatory.email', signatory.email ?? '');
                        setValue('signatureSameAsContact', signatory.id === currentContact?.id);
                      }}
                    />
                  </FormControl>
                )}
              />
              <Controller
                control={control}
                name="signatory.email"
                render={({ field, formState }) => (
                  <FormControl disabled={!currentSignatory} error={Boolean(formState.errors.signatory?.email)}>
                    <FormLabel>
                      <Trans id="Email" />
                    </FormLabel>

                    <Input startDecorator={<EmailIcon />} {...field} value={field.value ?? ''} />
                  </FormControl>
                )}
              />

              <Controller
                control={control}
                name="signatory.phone"
                render={({ field, formState }) => (
                  <FormControl disabled={!currentSignatory} error={Boolean(formState.errors.signatory?.phone)}>
                    <FormLabel>
                      <Trans id="Phone" />
                    </FormLabel>

                    <Input startDecorator={<PhoneIcon />} {...field} value={field.value ?? ''} />
                  </FormControl>
                )}
              />
            </>
          )}
          <Box>
            {error && <GenericError />}
            <Button color={success ? 'success' : 'primary'} type="submit" loading={loading}>
              {submitLabel}
            </Button>
          </Box>
        </Stack>
      </form>
    </Stack>
  );
};
